// useOperatorEfficiency — Fetches operator efficiency scores for a product.
// Returns a map of employee_number → OperatorEfficiency, loading state, and sort control.

import { useState, useCallback, useEffect, useRef } from 'react';
import productionService, { OperatorEfficiency } from '../services/productionService';

export type EfficiencySortBy = 'yield' | 'speed' | 'both';

export function useOperatorEfficiency(productSku: string | undefined | null) {
  const [efficiencyMap, setEfficiencyMap] = useState<Map<string, OperatorEfficiency>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState<EfficiencySortBy>('both');
  const lastFetchedRef = useRef<string>('');

  const fetchEfficiency = useCallback(async () => {
    if (!productSku) {
      setEfficiencyMap(new Map());
      return;
    }

    // Avoid re-fetching for the same product + sort
    const cacheKey = `${productSku}|${sortBy}`;
    if (lastFetchedRef.current === cacheKey) return;

    setIsLoading(true);
    try {
      const data = await productionService.getOperatorEfficiency(productSku, sortBy);
      const map = new Map<string, OperatorEfficiency>();
      data.operators.forEach(op => map.set(op.employee_number, op));
      setEfficiencyMap(map);
      lastFetchedRef.current = cacheKey;
    } catch (err) {
      console.error('Failed to fetch operator efficiency:', err);
      setEfficiencyMap(new Map());
    } finally {
      setIsLoading(false);
    }
  }, [productSku, sortBy]);

  useEffect(() => {
    fetchEfficiency();
  }, [fetchEfficiency]);

  return { efficiencyMap, isLoading, sortBy, setSortBy, refetch: fetchEfficiency };
}
